import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Expand, X } from 'lucide-react';

const IMG = '/masterplan/aravi-masterplan.jpg';
const IMG_PREVIEW = '/masterplan/aravi-masterplan-preview.jpg';
const PDF = '/masterplan/aravi-masterplan.pdf';

export const MasterplanSection = () => {
  const [open, setOpen] = useState(false);

  // Lock body scroll while lightbox open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <section
      data-testid="masterplan-section"
      className="bg-[#18342A] text-[#F5F1E8] py-24 lg:py-32 grain-overlay overflow-hidden"
    >
      <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto relative z-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-end mb-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-[#C9A458]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A458]">
                The Masterplan
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight font-light">
              Every road, every plot,<br />every tree — accounted for.
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-4 text-[#F5F1E8]/70 text-base leading-relaxed font-light"
          >
            Our sanctioned layout is publicly documented. Explore the full
            plot arrangement, roads, amenity zones and the Miyawaki forest belt
            — the same drawing we file, verbatim.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative group"
        >
          <button
            type="button"
            data-testid="masterplan-open-button"
            onClick={() => setOpen(true)}
            aria-label="Open full masterplan"
            className="block w-full text-left overflow-hidden bg-[#F5F1E8]"
          >
            <img
              src={IMG_PREVIEW}
              alt="Aravi Eco Homes — sanctioned masterplan"
              loading="lazy"
              className="w-full h-auto object-contain transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.02]"
            />
            <span className="pointer-events-none absolute top-5 right-5 inline-flex items-center gap-2 bg-[#18342A]/85 backdrop-blur-md text-[#F5F1E8] text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border border-[#C9A458]/30 group-hover:bg-[#C9A458] group-hover:text-[#18342A] group-hover:border-[#C9A458] transition-colors duration-500">
              <Expand size={14} strokeWidth={1.6} />
              View Full
            </span>
          </button>
        </motion.div>

        {/* Actions */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            type="button"
            data-testid="masterplan-view-fullscreen"
            onClick={() => setOpen(true)}
            className="group inline-flex items-center gap-3 bg-[#C9A458] text-[#18342A] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#F5F1E8] transition-colors"
          >
            View at Full Size
            <Expand size={14} strokeWidth={1.6} className="group-hover:scale-110 transition-transform" />
          </button>
          <a
            href={PDF}
            download
            data-testid="masterplan-download-pdf"
            className="group inline-flex items-center gap-3 border border-[#F5F1E8]/40 text-[#F5F1E8] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:border-[#C9A458] hover:text-[#C9A458] transition-colors"
          >
            Download Layout (PDF)
            <Download size={14} strokeWidth={1.6} className="group-hover:translate-y-0.5 transition-transform" />
          </a>
          <span className="sm:ml-auto text-[10px] tracking-[0.4em] uppercase text-[#9BA58D]">
            HMDA Limits · Public document
          </span>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          data-testid="masterplan-lightbox"
          className="fixed inset-0 z-[100] bg-[#0d1d17]/95 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Masterplan full view"
        >
          <button
            type="button"
            data-testid="masterplan-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label="Close masterplan"
            className="fixed top-5 right-5 z-[110] w-12 h-12 grid place-items-center border border-[#F5F1E8]/40 text-[#F5F1E8] hover:bg-[#C9A458] hover:text-[#18342A] hover:border-[#C9A458] transition-colors"
          >
            <X size={20} strokeWidth={1.4} />
          </button>
          <div className="fixed top-5 left-5 z-[110] text-[10px] tracking-[0.4em] uppercase text-[#C9A458]">
                        Aravi · Sanctioned Layout
          </div>
          <div
            className="h-full w-full overflow-auto p-6 md:p-12 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={IMG}
              alt="Aravi Eco Homes — sanctioned masterplan (full resolution)"
              className="max-w-none w-auto h-auto"
              style={{ maxHeight: 'none' }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default MasterplanSection;
